import clsx from 'clsx';
import React from 'react';
import { SanityBlock, SanityLink } from '../../../../graphql-types';
import { Typography } from '../atoms/typography';
import { RichText } from '../molecules/rich-text';
import { Container } from '../atoms/container';
import { SanityLinkList } from '../molecules/sanity-link-list';

export type Layout = '1' | '2' | '3';

type Background = 'dark' | 'light' | 'np-green';

type TitleSize = 'default' | 'large';

type TextBlockProps = {
    className?: string;
    title: string;
    description?: SanityBlock[];
    links?: SanityLink[];
    contentCards?: {
        title: string;
        content?: SanityBlock[];
        links?: SanityLink[];
    }[];
    titleSize?: TitleSize;
    background?: Background;
    layout?: Layout;
};

export function TextBlock({
    title,
    description,
    links,
    titleSize = 'default',
    contentCards,
    background = 'light',
    className,
    layout = '1',
}: TextBlockProps) {
    const contentCardCount = contentCards?.length || 0;

    return (
        <Container className={className}>
            {title || description ? (
                <div className={clsx('mb-6 lg:mb-8')}>
                    <Typography
                        variant={titleSize === 'large' ? 'hd-3xl' : 'hd-lg'}
                        desktopVariant={titleSize === 'large' ? 'hd-4xl' : 'hd-lg'}
                        as="h2"
                    >
                        {titleSize === 'large' ? title : <strong>{title}</strong>}
                    </Typography>
                    {description ? (
                        <RichText
                            value={description}
                            className={clsx({
                                'mt-3 md:mt-4 lg:mt-6': !!title,
                            })}
                        />
                    ) : null}

                    {links && links.length > 0 ? (
                        <SanityLinkList
                            links={links}
                            size="md"
                            className="mt-8"
                            isBackgroundDark={background === 'dark'}
                        />
                    ) : null}
                </div>
            ) : null}

            {contentCardCount > 0 ? (
                <ul
                    className={clsx('grid gap-8 lg:gap-y-16 xl:gap-y-20', {
                        'xl:grid-cols-2 max-w-[784px] xl:max-w-full': layout === '1',
                        'lg:grid-cols-2': layout === '2',
                        'lg:grid-cols-3': layout === '3',
                    })}
                >
                    {contentCards?.map((contentCard, index) => {
                        return (
                            <li key={index}>
                                <ContentCard
                                    {...contentCard}
                                    background={background}
                                    titleLevel={title ? 'h3' : 'h2'}
                                />
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </Container>
    );
}

type ContentCardProps = {
    title: string;
    content?: SanityBlock[];
    links?: SanityLink[];
    titleSize?: TitleSize;
    titleLevel?: 'h2' | 'h3';
    background?: Background;
};

const ContentCard = ({
    title,
    content,
    links,
    titleLevel = 'h3',
    titleSize = 'default',
    background = 'light',
}: ContentCardProps) => {
    return (
        <article>
            <Typography
                variant={titleSize === 'large' ? 'hd-3xl' : 'hd-lg'}
                desktopVariant={titleSize === 'large' ? 'hd-4xl' : 'hd-lg'}
                className="mb-3 md:mb-4 lg:mb-6"
                as={titleLevel}
            >
                {titleSize === 'large' ? title : <strong>{title}</strong>}
            </Typography>
            {content ? <RichText value={content} /> : null}
            <SanityLinkList
                links={links}
                size="md"
                className="mt-8"
                isBackgroundDark={background === 'dark'}
            />
        </article>
    );
};
